import { useEffect, useState, useCallback } from "react";
import { Loader2, ShoppingCart, RefreshCw } from "lucide-react";
import { Contract, JsonRpcProvider, formatEther } from "ethers";
import { useWallet } from "../contexts/WalletContext";
import { NFT_ADDRESS, MARKETPLACE_ADDRESS, PhygitalNFTAbi, NFTMarketplaceAbi, DEPLOYED_CHAIN_ID } from "../contracts";
import { fetchMetadata } from "../utils/ipfs";
import { POLYGON_RPC_URL } from "../config/web3Config";
import NFTImage from "../components/NFTImage";
import Toast from "../components/Toast";

interface NFTItem {
  tokenId: bigint;
  seller:  string;
  price:   bigint;
  name:    string;
  description: string;
  image:   string;
  isGenesis: boolean;
}
interface ToastState { message: string; type: "success"|"error"|"info"|"pending"; txHash?: string; }

function getProvider() {
  return new JsonRpcProvider(POLYGON_RPC_URL);
}

export default function HomePage() {
  const { isConnected, signer, chainId, walletReady } = useWallet();
  const [nfts,    setNfts]    = useState<NFTItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [buying,  setBuying]  = useState<bigint | null>(null);
  const [toast,   setToast]   = useState<ToastState | null>(null);

  const loadNFTs = useCallback(async () => {
    try {
      setSyncing(true);
      const provider = getProvider();
      const nft = new Contract(NFT_ADDRESS, PhygitalNFTAbi, provider);
      const mkt = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, provider);
      const total: bigint = await nft.totalMinted();
      const items: NFTItem[] = [];
      for (let id = 1n; id <= total; id++) {
        const listing = await mkt.getListing(NFT_ADDRESS, id) as { seller: string; price: bigint; active: boolean };
        if (!listing.active) continue;
        const uri: string = await nft.tokenURI(id);
        const meta = await fetchMetadata(uri);
        items.push({ tokenId: id, seller: listing.seller, price: listing.price,
          name: meta.name, description: meta.description, image: meta.image, isGenesis: id === 1n });
      }
      setNfts(items);
    } catch (e) { console.error("loadNFTs:", e); }
    finally { setLoading(false); setSyncing(false); }
  }, []);

  useEffect(() => { if (walletReady) loadNFTs(); }, [walletReady, loadNFTs]);

  const buyNFT = async (item: NFTItem) => {
    if (!signer) { setToast({ message: "Connect your wallet to buy", type: "error" }); return; }
    try {
      setBuying(item.tokenId);
      setToast({ message: "Confirm purchase in MetaMask…", type: "pending" });
      const mkt = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, signer);
      const tx  = await mkt.buyNFT(NFT_ADDRESS, item.tokenId, { value: item.price });
      setToast({ message: "Transaction submitted…", type: "pending", txHash: tx.hash });
      await tx.wait();
      setToast({ message: "NFT purchased!", type: "success", txHash: tx.hash });
      loadNFTs();
    } catch (e: any) {
      setToast({ message: e?.reason ?? e?.message ?? "Purchase failed", type: "error" });
    } finally { setBuying(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">Loading marketplace…</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {toast && <Toast {...toast} chainId={chainId || DEPLOYED_CHAIN_ID} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-800">NFT Marketplace</h1>
        <button onClick={loadNFTs} disabled={syncing}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Loading…" : "Refresh"}
        </button>
      </div>

      {nfts.length === 0 ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="text-center">
            <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Items Listed</h2>
            <p className="text-gray-500">Check back later for new NFTs!</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {nfts.map(nft => (
            <div key={nft.tokenId.toString()}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              {nft.isGenesis && (
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold text-center py-1 tracking-widest uppercase">
                  ◆ Genesis NFT
                </div>
              )}
              <NFTImage tokenId={Number(nft.tokenId)} image={nft.image} name={nft.name} isGenesis={nft.isGenesis} />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 truncate text-base mb-0.5">{nft.name}</h3>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-3 truncate">
                  nftmarketplace.ca
                </p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Price</p>
                    <p className="text-base font-bold text-blue-600">
                      {parseFloat(formatEther(nft.price)).toFixed(nft.price % 1000000000000000n === 0n ? 1 : 4)} POL
                    </p>
                  </div>
                  <button
                    onClick={() => buyNFT(nft)}
                    disabled={buying === nft.tokenId || !isConnected}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {buying === nft.tokenId
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Buying…</>
                      : "Buy"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
